import React, { useState, useCallback, useEffect } from 'react';
import { Layout, Row, Col, Card, Divider, Spin, Alert, Typography, Space, Button, Tooltip, Tabs, Modal, Form, Input, InputNumber, Avatar, Switch, App, Tag, Badge } from 'antd';
import { ReloadOutlined, SettingOutlined, BarChartOutlined, RiseOutlined, LineChartOutlined, DotChartOutlined, LogoutOutlined } from '@ant-design/icons';
import StockSearch from './StockSearch';
import ParameterSettings from './ParameterSettings';
import { PriceChart } from './Charts';
import type { PriceData, IndicatorData } from './Charts/types';
import AnalysisResults from './AnalysisResults';
const AdvancedCandlestickChart = React.lazy(() => import('./Charts/AdvancedCandlestickChart'));
const TechnicalIndicatorChart = React.lazy(() => import('./Charts/TechnicalIndicatorChart'));
const CompositeChart = React.lazy(() => import('./Charts/CompositeChart'));
import './ThreeColumnLayout.css';
import { exportService, type ExportOptions } from '../services/export';
const RecommendationsPanel = React.lazy(() => import('./RecommendationsPanel'));
import { startBackends, isTauriAvailable } from '../utils/tauriBridge';
import { analysisApi } from '../services/api';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { Sider, Content } = Layout;

// 接口定义
interface StockInfo {
  symbol: string;
  name: string;
  market: string;
  price?: number;
  change?: number;
  changePercent?: number;
}

// 与参数面板的策略权重结构保持一致（M1+M2策略权重）
interface StrategyWeights {
  trend_following: number;
  mean_reversion: number;
  statistical_arbitrage: number;
  volatility_trading: number;
  market_sentiment: number;
  ml_forecasting: number;
  money_flow: number;
  risk_parity: number;
  sector_rotation: number;
  fundamental_quant: number;
  enhanced_event: number;
  event_driven: number;
  grid_trading: number;
  factor_investing: number;
}

interface AnalysisParameters {
  dateRange: [any, any] | null;
  strategyWeights: StrategyWeights;
  selectedStrategies: (keyof StrategyWeights)[];
  autoUpdate: boolean;
  updateInterval: number;
}

interface AnalysisResult {
  symbol: string;
  company_name: string;
  timestamp: string;
  overall_signal: 'BUY' | 'SELL' | 'HOLD';
  display_action?: string; // 新增：显示动作（如“观望”、“谨慎买入”）
  confidence: number;
  risk_level: 'LOW' | 'MEDIUM' | 'HIGH';
  expected_return: number;
  // 策略加权的日收益（百分比，优先口径）
  expected_return_weighted?: number;
  strategies: any[];
  strategies_all?: any[];
  market_data: any;
  technical_indicators: any;
  risk_metrics: any;
  // 组聚合结果（可选），用于“组维度”视图展示
  group_contributions?: Record<string, {
    group_weight: number;
    group_score: number;
    group_confidence: number;
    group_risk: number;
  }>;
  // 近N日预测收益（可选）：例如 {5: number, 10: number, 20: number}
  expected_returns_periods?: Record<number, number>;
  // 策略加权的近N日预测收益（百分比，优先口径）：例如 {5: number, 10: number, 20: number}
  expected_returns_periods_weighted?: Record<number, number>;
  // 投资摘要（可选）
  investment_summary?: any;
}

// 移除KlineData接口，直接使用PriceData

const ThreeColumnLayout: React.FC = () => {
  // 状态管理
  const [selectedStock, setSelectedStock] = useState<StockInfo | null>(null);
  const [analysisParameters, setAnalysisParameters] = useState<AnalysisParameters | null>(null);
  // 是否显示“数据不足”的占位策略
  const [showPlaceholders, setShowPlaceholders] = useState<boolean>(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [klineData, setKlineData] = useState<PriceData[]>([]);
  const [indicatorData, setIndicatorData] = useState<IndicatorData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [strategyLoading, setStrategyLoading] = useState<boolean>(false);
  const [klineLoading, setKlineLoading] = useState<boolean>(false);
  const analysisSeqRef = React.useRef(0);
  const klineSeqRef = React.useRef(0);
  const [error, setError] = useState<string>('');
  const [leftCollapsed, setLeftCollapsed] = useState<boolean>(false);
  const [rightCollapsed, setRightCollapsed] = useState<boolean>(false);
  const [autoUpdateTimer, setAutoUpdateTimer] = useState<NodeJS.Timeout | null>(null);
  const [settingsVisible, setSettingsVisible] = useState<boolean>(false);
  const [form] = Form.useForm();
  const [tauriAvailable, setTauriAvailable] = useState<boolean>(false);
  const initialToken = React.useMemo(() => (typeof window !== 'undefined' ? (localStorage.getItem('TUSHARE_TOKEN') || '') : ''), []);
  const initialTTL = React.useMemo(() => (typeof window !== 'undefined' ? (localStorage.getItem('FIN_VAL_TTL') || '900') : '900'), []);
  const [exportPeriod, setExportPeriod] = useState<number>(20);
  const [backendReady, setBackendReady] = useState<boolean>(false);
  const [mcpReady, setMcpReady] = useState<boolean>(false);
  const [hasToken, setHasToken] = useState<boolean>(false);
  const healthInitRef = React.useRef(false);
  
  // 技术指标相关状态
  const [showVolume, setShowVolume] = useState<boolean>(true);
  const [showIndicators, setShowIndicators] = useState<boolean>(true);
  const [indicatorTypes, setIndicatorTypes] = useState<string[]>(['ma5', 'ma20', 'ma50', 'rsi']);
  const [activeChartTab, setActiveChartTab] = useState<string>('advanced');
  const exportFormat: 'pdf' = 'pdf';
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [backtestLoading, setBacktestLoading] = useState<boolean>(false);
  const [backtestParams, setBacktestParams] = useState<{ percents: number; stop_loss_dist: number; sma_period: number; rsi_period: number; benchmark: string }>({ percents: 45, stop_loss_dist: 0.05, sma_period: 200, rsi_period: 14, benchmark: '000300.SZ' });
  const [backtestResult, setBacktestResult] = useState<any>(null);
  

  const handleLogout = useCallback(() => {
    localStorage.removeItem('auth_token');
    message.success('已退出登录');
    navigate('/login');
  }, [navigate, message]);

  // API基础URL（优先Vite变量，其次CRA，最终回退到相对路径以走Vite代理）
  const getApiBaseUrl = () => {
    // Check if we are in dev mode (localhost/127.0.0.1) and using the Vite dev server port (usually 3000-3010)
    // If so, we should use the relative path '/api' to let Vite proxy handle it.
    // Otherwise (production build served by Nginx), we also use '/api' to let Nginx proxy handle it.
    // The only case we might need a full URL is if the backend is on a different domain without proxy.
    
    // For our setup (Vite Proxy in Dev, Nginx Proxy in Prod), always using '/api' is the safest bet.
    // This avoids issues where env vars might be missing or incorrect.
    return '/api';
  };
  const API_BASE_URL = getApiBaseUrl();

  // 数据转换函数：将后端返回的数据格式转换为前端期望的格式
  const transformAnalysisData = useCallback((backendData: any, financialData?: any): AnalysisResult => {
    console.log('转换策略分析数据:', backendData);
    
    // 提取策略数据 - 优先使用统一引擎返回的 strategies；仅当不存在时才使用 signals（兼容旧格式）
    const strategies: any[] = [];
    const signalsData = backendData.strategies || backendData.strategies_all || backendData.signals || [];
    if (Array.isArray(signalsData)) {
      signalsData.forEach((strategy: any) => {
        // 统一信号类型为 BUY/SELL/HOLD；兼容 signal 为对象 { signal_type, confidence, strength }
        let sig: string = 'HOLD';
        const sigField = strategy.signal || strategy.signal_type; // 可能是字符串或对象
        if (typeof sigField === 'string') {
          sig = sigField.toUpperCase();
        } else if (sigField && typeof sigField === 'object') {
          const st = sigField.signal_type || sigField.type;
          if (typeof st === 'string') sig = st.toUpperCase();
        }
        // 兼容后端字段：优先 analysis_details.reason，其次顶层 reason/description/reasoning
        const reason = (strategy.analysis_details && (strategy.analysis_details.reason || strategy.analysis_details.summary))
          || strategy.reasoning || strategy.reason || strategy.description || '';
        strategies.push({
          strategy_type: strategy.strategy_type || strategy.name || '',
          name: strategy.name || strategy.strategy_type || '', // Ensure name is populated for PDF export
          signal: (sig === 'BUY' || sig === 'SELL' || sig === 'HOLD') ? sig : 'HOLD',
          confidence: (typeof strategy.confidence === 'number' ? strategy.confidence
            : (strategy.signal && typeof strategy.signal.confidence === 'number' ? strategy.signal.confidence
              : (typeof strategy.confidence_score === 'number' ? strategy.confidence_score / 100 : 0))),
          weight: (typeof strategy.weight === 'number' ? strategy.weight : (typeof strategy.position_size === 'number' ? strategy.position_size : 0)),
          expected_return: strategy.expected_return || 0,
          risk_score: strategy.risk_reward_ratio || strategy.risk_score || 0,
          reason,
          // 为 AnalysisResults 组件的“说明”列提供 analysis_details.reason 入口
          analysis_details: {
            ...(strategy.analysis_details || {}),
            reason
          }
        });
      });
    }

    // 转换市场数据 - 适配策略分析API的数据结构
    const marketData = backendData.market_data || {};
    const marketSummary = backendData.market_data_summary || {};
    const priceRange = marketSummary.price_range || {};
    const priceData = backendData.price_data || [];
    
    // 计算价格变动：优先使用后端提供的 market_data.price_change/price_change_percent
    let priceChange = Number(marketData.price_change ?? 0);
    let priceChangePercent = Number(marketData.price_change_percent ?? 0);
    
    // 若后端未提供或为0（可能仅返回1条历史数据），再根据价格序列回退计算
    if ((priceChange === 0 && priceChangePercent === 0) && priceData.length >= 2) {
      const latestPrice = Number(priceData[priceData.length - 1]?.close ?? marketData.current_price ?? 0);
      const previousPrice = Number(priceData[priceData.length - 2]?.close ?? latestPrice);
      const delta = latestPrice - previousPrice;
      priceChange = delta;
      priceChangePercent = previousPrice !== 0 ? (delta / previousPrice) * 100 : 0;
    }
    
    // 以最新一条收盘价为“当前价格”；如后端提供当天价格区间则优先使用
    const latestClose = priceData.length > 0 ? Number(priceData[priceData.length - 1]?.close) : undefined;
    const latestHigh = priceData.length > 0 ? Number(priceData[priceData.length - 1]?.high) : undefined;
    const latestLow = priceData.length > 0 ? Number(priceData[priceData.length - 1]?.low) : undefined;

    const transformedMarketData = {
      // 优先使用最新K线的收盘价，其次后端 market_data 提供的 current_price
      current_price: (latestClose ?? marketData.current_price ?? priceRange.current ?? 0),
      // 当日最高/最低优先使用最新K线数据；若无则回退到后端字段
      high_price: (latestHigh ?? marketData.high_price ?? priceRange.high ?? 0),
      low_price: (latestLow ?? marketData.low_price ?? priceRange.low ?? 0),
      price_change: priceChange,
      price_change_percent: priceChangePercent,
      // 成交量：优先后端字段，若为0/缺失则回退到最新K线的成交量
      volume: (marketData.volume ?? (priceData.length > 0 ? Number(priceData[priceData.length - 1]?.volume || 0) : 0)),
      market_cap: marketData.market_cap || 0,
      pe_ratio: marketData.pe_ratio || 0,
      pb_ratio: marketData.pb_ratio || 0,
      dividend_yield: marketData.dividend_yield || 0,
      float_market_cap: marketData.float_market_cap || 0,
      turnover_rate: marketData.turnover_rate || 0
    };

    // 使用财务端点数据覆盖估值字段（如存在）
    const finSummary = marketSummary.financial_data || {};
    if (financialData && typeof financialData === 'object') {
      const fin = financialData as any;
      transformedMarketData.market_cap = fin.market_cap ?? transformedMarketData.market_cap;
      transformedMarketData.float_market_cap = fin.float_market_cap ?? transformedMarketData.float_market_cap;
      transformedMarketData.pe_ratio = fin.pe_ratio ?? transformedMarketData.pe_ratio;
      transformedMarketData.pb_ratio = fin.pb_ratio ?? transformedMarketData.pb_ratio;
      transformedMarketData.dividend_yield = fin.dividend_yield ?? transformedMarketData.dividend_yield;
      transformedMarketData.turnover_rate = fin.turnover_rate ?? transformedMarketData.turnover_rate;
    }
    // 若独立财务端点未返回，使用分析响应中的财务摘要（已统一为“亿元”）
    transformedMarketData.market_cap = finSummary.market_cap ?? transformedMarketData.market_cap;
    transformedMarketData.float_market_cap = finSummary.float_market_cap ?? transformedMarketData.float_market_cap;
    transformedMarketData.pe_ratio = finSummary.pe_ratio ?? transformedMarketData.pe_ratio;
    transformedMarketData.pb_ratio = finSummary.pb_ratio ?? transformedMarketData.pb_ratio;
    transformedMarketData.dividend_yield = finSummary.dividend_yield ?? transformedMarketData.dividend_yield;
    transformedMarketData.turnover_rate = finSummary.turnover_rate ?? transformedMarketData.turnover_rate;

    // 转换技术指标 - 适配策略分析API的数据结构
    const techIndicators = backendData.technical_indicators || {};
    
    // 获取最新的技术指标值
    const getLatestValue = (indicatorData: any, defaultValue = 0) => {
      if (typeof indicatorData === 'number') {
        return indicatorData;
      }
      if (Array.isArray(indicatorData) && indicatorData.length > 0) {
        return indicatorData[indicatorData.length - 1] || defaultValue;
      }
      if (indicatorData && typeof indicatorData === 'object' && indicatorData.current !== undefined) {
        return indicatorData.current;
      }
      return defaultValue;
    };
    
    // 获取数组最后一个值的辅助函数
    const getArrayLastValue = (arr: any, defaultValue = 0) => {
      if (Array.isArray(arr) && arr.length > 0) {
        return arr[arr.length - 1] || defaultValue;
      }
      return defaultValue;
    };
    
    const technicalIndicators = {
      // RSI使用current字段
      rsi: techIndicators.rsi?.current || 0,
      macd: {
        // MACD使用数组的最后一个值
        macd: getArrayLastValue(techIndicators.macd?.macd_line),
        signal: getArrayLastValue(techIndicators.macd?.signal_line),
        histogram: getArrayLastValue(techIndicators.macd?.histogram)
      },
      bollinger_bands: {
        // 布林带使用数组的最后一个值
        upper: getArrayLastValue(techIndicators.bollinger_bands?.upper),
        middle: getArrayLastValue(techIndicators.bollinger_bands?.middle),
        lower: getArrayLastValue(techIndicators.bollinger_bands?.lower)
      },
      moving_averages: {
        // 移动平均线使用数组的最后一个值
        ma5: getArrayLastValue(techIndicators.ma?.ma5),
        ma10: getArrayLastValue(techIndicators.ma?.ma10),
        ma20: getArrayLastValue(techIndicators.ma?.ma20),
        ma50: getArrayLastValue(techIndicators.ma?.ma50)
      }
    };

    // 转换风险指标 - 适配策略分析API的数据结构
    const backendRiskMetrics = backendData.risk_metrics || {};
    const normPct = (v: any): number => {
      const n = Number(v);
      if (!isFinite(n) || isNaN(n)) return 0;
      // 比例/百分值归一化到 0-1 区间
      if (Math.abs(n) > 1) return n / 100;
      return n;
    };
    const riskMetrics = {
      volatility: normPct(backendRiskMetrics.volatility),
      beta: Number(backendRiskMetrics.beta || 0),
      sharpe_ratio: Number(backendRiskMetrics.sharpe_ratio || 0),
      max_drawdown: normPct(backendRiskMetrics.max_drawdown),
      var_95: normPct(backendRiskMetrics.var_95 || backendRiskMetrics.value_at_risk || 0),
      // CVaR 映射修复：后端字段为 cvar_95，兼容旧字段名称
      cvar_95: normPct(
        backendRiskMetrics.cvar_95
        || backendRiskMetrics.expected_shortfall
        || backendRiskMetrics.conditional_var
        || 0
      ),
      information_ratio: Number(backendRiskMetrics.information_ratio || 0),
      sortino_ratio: Number(backendRiskMetrics.sortino_ratio || 0),
      calmar_ratio: Number(backendRiskMetrics.calmar_ratio || 0)
    };

    // 转换信号类型
    const signalMapping: { [key: string]: 'BUY' | 'SELL' | 'HOLD' } = {
      'buy': 'BUY',
      'sell': 'SELL',
      'hold': 'HOLD',
      'BUY': 'BUY',
      'SELL': 'SELL',
      'HOLD': 'HOLD'
    };

    // 转换风险等级
    const riskLevelMapping: { [key: string]: 'LOW' | 'MEDIUM' | 'HIGH' } = {
      'low': 'LOW',
      'medium': 'MEDIUM',
      'high': 'HIGH',
      'LOW': 'LOW',
      'MEDIUM': 'MEDIUM',
      'HIGH': 'HIGH'
    };

    // 从策略分析API数据中提取总体推荐（优先使用统一字段 overall_signal）
    const overallSignalRaw = backendData.overall_signal || '';
    const overallRecommendation = backendData.overall_recommendation || '';
    const performanceMetrics = backendData.performance_metrics || {};
    const riskAssessment = backendData.risk_assessment || '';
    
    // 解析总体推荐信号：优先后端 weighted_result.recommendation（action），其次 overall_signal
    let overallSignal: 'BUY' | 'SELL' | 'HOLD' = 'HOLD';
    const rec = backendData.recommendation || overallRecommendation || '';
    if (typeof rec === 'string') {
      const r = rec.toUpperCase();
      if (r.includes('买') || r.includes('BUY')) overallSignal = 'BUY';
      else if (r.includes('卖') || r.includes('SELL')) overallSignal = 'SELL';
      else overallSignal = 'HOLD';
    } else if (typeof overallSignalRaw === 'string' && ['BUY','SELL','HOLD'].includes(overallSignalRaw)) {
      overallSignal = overallSignalRaw as any;
    }
    
    // 解析风险等级：优先后端 weighted_result.risk_level（数值0-1），其次 risk_assessment 文本
    let riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' = 'MEDIUM';
    const riskLevelRaw = backendData.risk_level;
    if (typeof riskLevelRaw === 'number') {
      if (riskLevelRaw < 0.33) riskLevel = 'LOW';
      else if (riskLevelRaw > 0.66) riskLevel = 'HIGH';
      else riskLevel = 'MEDIUM';
    } else if (typeof riskAssessment === 'string') {
      const ra = riskAssessment.trim().toUpperCase();
      if (ra.includes('低') || ra.includes('LOW')) riskLevel = 'LOW';
      else if (ra.includes('高') || ra.includes('HIGH')) riskLevel = 'HIGH';
      else riskLevel = 'MEDIUM';
    }
    
    return {
      symbol: backendData.symbol || '',
      company_name: backendData.company_name || backendData.symbol || '',
      timestamp: backendData.timestamp || new Date().toISOString(),
      overall_signal: overallSignal,
      display_action: backendData.display_action || (backendData.recommendation && typeof backendData.recommendation === 'string' ? backendData.recommendation : undefined),
      confidence: (typeof backendData.confidence === 'number' ? backendData.confidence : (performanceMetrics['信号强度'] || performanceMetrics.confidence || 0)),
      risk_level: riskLevel,
      expected_return: (typeof backendData.expected_return === 'number' ? backendData.expected_return : (performanceMetrics['预期收益率'] || performanceMetrics.expected_return || 0)),
      strategies: strategies,
      strategies_all: backendData.strategies_all || [],
      market_data: transformedMarketData,
      technical_indicators: technicalIndicators,
      risk_metrics: riskMetrics,
      group_contributions: backendData.group_contributions || {},
      expected_returns_periods: backendData.expected_returns_periods || {},
      expected_returns_periods_weighted: backendData.expected_returns_periods_weighted || {},
      expected_return_weighted: backendData.expected_return_weighted,
      investment_summary: backendData.investment_summary // 传递投资摘要
    };
  }, []);

    const runBacktest = useCallback(async () => {
    if (!selectedStock) {
      message.error('请先选择股票');
      return;
    }
    try {
      setBacktestLoading(true);
      const dr = analysisParameters?.dateRange || null;
      const fmt = (d: any) => {
        try { return d?.format ? d.format('YYYY-MM-DD') : new Date(d).toISOString().slice(0,10); } catch { return undefined; }
      };
        const body = {
          symbols: [selectedStock.symbol],
          start_date: fmt(dr?.[0]) || undefined,
          end_date: fmt(dr?.[1]) || undefined,
          days: (() => {
            if (dr && dr[0] && dr[1]) {
              try {
                const s = dr[0]; const e = dr[1];
                const sd = s?.toDate ? s.toDate() : new Date(s);
                const ed = e?.toDate ? e.toDate() : new Date(e);
                const diff = Math.max(1, Math.round((ed.getTime() - sd.getTime()) / (24*3600*1000)));
                return diff;
              } catch { return undefined; }
            }
            return undefined;
          })(),
          period: '1d',
          percents: backtestParams.percents,
          stop_loss_dist: backtestParams.stop_loss_dist,
          sma_period: backtestParams.sma_period,
          rsi_period: backtestParams.rsi_period,
          benchmark: backtestParams.benchmark
        };
        // 启用快速模式，提升回测响应速度
        (body as any).fast = true;
      const headers: Record<string,string> = { 'Content-Type': 'application/json' };
      const tk = (typeof localStorage !== 'undefined' ? (localStorage.getItem('TUSHARE_TOKEN') || '') : '');
      if (tk) headers['X-Tushare-Token'] = tk;
      const asyncResp = await fetch(`${API_BASE_URL}/backtest/run_async`, { method: 'POST', headers, body: JSON.stringify(body) });
      if (asyncResp.ok) {
        const jobInfo = await asyncResp.json();
        const jobId = jobInfo?.jobId;
        if (!jobId) throw new Error('任务创建失败');
        let tries = 0;
        while (tries < 120) {
          await new Promise(r=>setTimeout(r, 1000));
          const st = await fetch(`${API_BASE_URL}/backtest/status/${jobId}`, { headers });
          if (!st.ok) break;
          const sj = await st.json();
          if (String(sj?.status) === 'completed') {
            const rr = await fetch(`${API_BASE_URL}/backtest/result/${jobId}`, { headers });
            if (!rr.ok) throw new Error(`获取结果失败: ${rr.status}`);
            const data = await rr.json();
            setBacktestResult(data);
            message.success('回测完成');
            return;
          }
          if (String(sj?.status) === 'failed') {
            const detail = sj?.error?.detail || '未知错误';
            throw new Error(detail);
          }
          tries += 1;
        }
        throw new Error('回测超时，请稍后重试');
      }
      const resp = await fetch(`${API_BASE_URL}/backtest/run`, { method: 'POST', headers, body: JSON.stringify(body) });
      if (!resp.ok) throw new Error(`回测失败: ${resp.status}`);
      const data = await resp.json();
      setBacktestResult(data);
      message.success('回测完成');
    } catch (e: any) {
      message.error(e?.message || '回测失败');
    } finally {
      setBacktestLoading(false);
    }
  }, [selectedStock, analysisParameters, backtestParams, API_BASE_URL, message]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (healthInitRef.current) return;
      healthInitRef.current = true;
      try {
        // 移除 AbortController 以避免控制台 ERR_ABORTED 报错
        const resp = await fetch(`${API_BASE_URL}/health`);
        if (!mounted) return;

        if (!resp.ok) {
          setBackendReady(false);
          setMcpReady(false);
          return;
        }
        const j: any = await resp.json();
        const ok = String(j?.status || '').toLowerCase() === 'healthy';
        setBackendReady(!!ok);
        setMcpReady(!!j?.services);
        try {
          const ts = await fetch(`${API_BASE_URL}/token_status`);
          if (!mounted) return;
          const tj = await ts.json().catch(() => ({}));
          const hasBackendToken = !!tj?.has_token;
          const localToken = (typeof window !== 'undefined' ? (localStorage.getItem('TUSHARE_TOKEN') || '') : '');
          const okToken = (localToken && localToken.trim().length > 0) || hasBackendToken;
          setHasToken(!!okToken);
        } catch { void 0; }
      } catch (e: any) {
        if (mounted) {
          setBackendReady(false);
          setMcpReady(false);
          const localToken = (typeof window !== 'undefined' ? (localStorage.getItem('TUSHARE_TOKEN') || '') : '');
          setHasToken(!!localToken && localToken.trim().length > 0);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [API_BASE_URL, settingsVisible]);

  const handleExport = useCallback(async (selectedPeriod?: number) => {
    try {
      if (!analysisResult) {
        message.info('暂无分析数据，无法导出');
        return;
      }
      const data = (analysisResult as any);

      // 从 klineData 计算性能数据
      let performanceData: any[] = [];
      if (klineData && klineData.length > 0) {
         let cumRet = 1.0;
         performanceData = klineData.map((item, idx) => {
            const prev = idx > 0 ? klineData[idx - 1] : item;
            const ret = prev.close > 0 ? (item.close - prev.close) / prev.close : 0;
            cumRet *= (1 + ret);
            return {
              timestamp: item.timestamp,
              returns: ret * 100,
              cumulative_returns: (cumRet - 1) * 100,
              volatility: Math.abs(ret) * 100 // Simplified
            };
         });
      }

      const exportData = exportService.generateExportData(
        data.symbol,
        data.company_name || data.symbol,
        {
          price_data: klineData || [], // Use actual klineData instead of data.price_data
          technical_indicators: data.technical_indicators || [],
          performance_data: performanceData.length > 0 ? performanceData : (data.performance_data || []),
          risk_data: data.risk_metrics ? [{
            timestamp: data.timestamp,
            var: data.risk_metrics.var_95,
            cvar: data.risk_metrics.cvar_95
          } as any] : [],
          portfolio_data: [],
          strategies: data.strategies || [],
          // 原始字段也一并传递，便于后端兜底一致化
          expected_returns_periods: (data as any)?.expected_returns_periods || {},
          expected_returns_periods_weighted: (data as any)?.expected_returns_periods_weighted || (data as any)?.expected_returns_periods || {},
          overview: {
            overall_signal: (data.overall_signal === 'BUY' ? '买入' : data.overall_signal === 'SELL' ? '卖出' : '观望'),
            confidence: data.confidence,
            risk_level: (data.risk_level === 'HIGH' ? '高' : data.risk_level === 'LOW' ? '低' : '中'),
            expected_return: (selectedPeriod && (data.expected_returns_periods_weighted as any)?.[selectedPeriod])
              || (selectedPeriod && (data.expected_returns_periods as any)?.[selectedPeriod])
              || (data.expected_returns_periods_weighted && (data.expected_returns_periods_weighted as any)[20])
              || (data.expected_returns_periods && (data.expected_returns_periods as any)[20])
              || data.expected_return || (data as any)?.expected_return_weighted || 0
          }
        }
      );
      // 将选择的周期传递给后端，便于在PDF中显示“预测收益（N日）”
      (exportData as any).expected_return_days = selectedPeriod || 20;
      const options: ExportOptions = { format: exportFormat, includeCharts: true, includeTables: true, includeAnalysis: true };
      await exportService.exportReport(exportData, options);
      message.success(`已导出${exportFormat.toUpperCase()}报告`);
    } catch (e: any) {
      message.error(`导出失败：${e?.message || '未知错误'}`);
    }
  }, [analysisResult, klineData]);

  // 获取历史K线数据函数
  const fetchKlineData = useCallback(async (stock: StockInfo, parameters: AnalysisParameters) => {
    if (!stock || !parameters) return;
    
    setKlineLoading(true);
    
    try {
      const seq = klineSeqRef.current + 1;
      klineSeqRef.current = seq;
      // 为MA50计算获取额外的历史数据（向前扩展50天）
      let startDate = parameters.dateRange?.[0];
      let extendedStartDate = startDate;
      
      // 如果选择了技术指标且包含MA50，则向前扩展50天数据
      if (indicatorTypes.includes('ma50') || indicatorTypes.includes('ma')) {
        extendedStartDate = startDate ? startDate.clone().subtract(50, 'days') : undefined;
      }
      
      // 构建请求参数
      const requestBody = {
        symbol: stock.symbol,
        start_date: extendedStartDate?.format('YYYY-MM-DD') || parameters.dateRange?.[0]?.format('YYYY-MM-DD'),
        end_date: parameters.dateRange?.[1]?.format('YYYY-MM-DD'),
        period: '1d'
      };
      
      // 并行获取K线数据和技术指标数据（确保RSI一致性）
      const klineReq = fetch(`${API_BASE_URL}/stocks/historical`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tushare-Token': (typeof window !== 'undefined' ? (localStorage.getItem('TUSHARE_TOKEN') || '') : '')
        },
        body: JSON.stringify(requestBody)
      });
      
      // 计算需要的历史天数
      let reqDays = 365;
      if (parameters.dateRange && parameters.dateRange[0]) {
        const start = dayjs(parameters.dateRange[0]);
        const end = parameters.dateRange[1] ? dayjs(parameters.dateRange[1]) : dayjs();
        reqDays = Math.max(365, end.diff(start, 'day') + 60); // 至少365天，或者时间范围+60天缓冲
      }

      // 获取后端计算的标准指标数据
      const techReq = fetch(`${API_BASE_URL}/analysis/technical?symbol=${stock.symbol}&days=${reqDays}`, {
        headers: {
          'X-Tushare-Token': (typeof window !== 'undefined' ? (localStorage.getItem('TUSHARE_TOKEN') || '') : '')
        }
      });

      const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), 30000));
      const [response, techResponse] = await Promise.all([
        Promise.race<[Response | null]>([klineReq as any, timeout as any]) as any,
        techReq.catch(() => null)
      ]);

      if (response === null) {
        console.warn('K线数据获取超时');
        setKlineData([]);
        setIndicatorData([]);
        return;
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('K线数据获取失败:', response.status, errorData);
        message.error(`历史数据获取失败: ${errorData.detail || response.statusText}`);
        throw new Error(errorData.detail || `获取历史数据失败: HTTP ${response.status}`);
      }
      
      const result = await response.json();
      const techResult = techResponse && techResponse.ok ? await techResponse.json() : null;
      
      // 转换数据格式为PriceData
      if (result.data && Array.isArray(result.data)) {
        const transformedData: PriceData[] = result.data.map((item: any) => ({
          timestamp: item.timestamp || item.date,
          value: parseFloat(item.close) || 0, // 使用收盘价作为value
          open: parseFloat(item.open) || 0,
          high: parseFloat(item.high) || 0,
          low: parseFloat(item.low) || 0,
          close: parseFloat(item.close) || 0,
          volume: parseFloat(item.volume) || 0
        }))
        // 按时间戳升序排序，确保时间轴正确（最早的时间在左侧）
        .sort((a: PriceData, b: PriceData) => {
          const timeA = new Date(a.timestamp).getTime();
          const timeB = new Date(b.timestamp).getTime();
          return timeA - timeB;
        });
        
        // 生成技术指标数据
        const indicators: IndicatorData[] = transformedData.map((item, index) => {
          const closePrices = transformedData.slice(Math.max(0, index - 59), index + 1).map(d => d.close);
          
          // 计算移动平均线
          const ma5 = index >= 4 ? closePrices.slice(-5).reduce((a, b) => a + b, 0) / 5 : undefined;
          const ma10 = index >= 9 ? closePrices.slice(-10).reduce((a, b) => a + b, 0) / 10 : undefined;
          const ma20 = index >= 19 ? closePrices.slice(-20).reduce((a, b) => a + b, 0) / 20 : undefined;
          const ma50 = index >= 49 ? closePrices.slice(-50).reduce((a, b) => a + b, 0) / 50 : undefined;
          
          // 计算RSI (优先使用后端标准算法)
          let rsi = undefined;
          const rsiSeries = techResult?.technical_indicators?.RSI?.series;
          if (Array.isArray(rsiSeries)) {
             // 对齐数据：假设两者都以最新日期结束
             const offset = transformedData.length - rsiSeries.length;
             if (index >= offset && index - offset >= 0) {
                 rsi = rsiSeries[index - offset];
             }
          } else if (index >= 14) {
            // Fallback: 前端简化计算
            const gains = [];
            const losses = [];
            for (let i = 1; i < closePrices.length; i++) {
              const change = closePrices[i] - closePrices[i - 1];
              gains.push(change > 0 ? change : 0);
              losses.push(change < 0 ? -change : 0);
            }
            const avgGain = gains.reduce((a, b) => a + b, 0) / gains.length;
            const avgLoss = losses.reduce((a, b) => a + b, 0) / losses.length;
            rsi = avgLoss === 0 ? 100 : 100 - (100 / (1 + avgGain / avgLoss));
          }
          
          // 计算MACD
          let macd = undefined;
          let macdSignal = undefined;
          let macdHistogram = undefined;
          const macdSeries = techResult?.technical_indicators?.MACD?.series;
          if (macdSeries && Array.isArray(macdSeries.macd)) {
             const len = macdSeries.macd.length;
             const offset = transformedData.length - len;
             if (index >= offset && index - offset >= 0) {
                 const idx = index - offset;
                 macd = macdSeries.macd[idx];
                 macdSignal = macdSeries.signal[idx];
                 macdHistogram = macdSeries.histogram[idx];
             }
          } else if (index >= 25) {
            // Fallback
            const ema12 = closePrices.slice(-12).reduce((a, b) => a + b, 0) / 12;
            const ema26 = closePrices.slice(-26) ? closePrices.slice(-26).reduce((a, b) => a + b, 0) / 26 : ema12;
            macd = ema12 - ema26;
            macdSignal = macd * 0.9;
            macdHistogram = macd - macdSignal;
          }
          
          // 计算布林带
          let upper_band = undefined;
          let middle_band = undefined;
          let lower_band = undefined;
          const bbSeries = techResult?.technical_indicators?.布林带?.series;
          if (bbSeries && Array.isArray(bbSeries.upper)) {
             const len = bbSeries.upper.length;
             const offset = transformedData.length - len;
             if (index >= offset && index - offset >= 0) {
                 const idx = index - offset;
                 upper_band = bbSeries.upper[idx];
                 middle_band = bbSeries.middle[idx];
                 lower_band = bbSeries.lower[idx];
             }
          } else if (ma20 !== undefined && index >= 19) {
            // Fallback
            const variance = closePrices.slice(-20).reduce((sum, price) => sum + Math.pow(price - ma20, 2), 0) / 20;
            const stdDev = Math.sqrt(variance);
            upper_band = ma20 + (2 * stdDev);
            middle_band = ma20;
            lower_band = ma20 - (2 * stdDev);
          }
          
          return {
            timestamp: item.timestamp,
            value: item.close,
            ma5,
            ma10,
            ma20,
            ma50,
            rsi,
            macd,
            signal: macdSignal,
            histogram: macdHistogram,
            upper_band,
            middle_band,
            lower_band,
            volume: item.volume
          };
        });
        
        // 过滤显示数据：只显示原始日期范围内的数据，但保留扩展数据用于技术指标计算
        let displayData = transformedData;
        let displayIndicators = indicators;
        
        // 如果有扩展开始日期，过滤显示数据
        if (extendedStartDate && parameters.dateRange?.[0]) {
          const originalStartTime = new Date(parameters.dateRange[0].format('YYYY-MM-DD')).getTime();
          displayData = transformedData.filter(item => new Date(item.timestamp).getTime() >= originalStartTime);
          displayIndicators = indicators.filter(item => new Date(item.timestamp).getTime() >= originalStartTime);
        }
        
        setKlineData(displayData);
        setIndicatorData(displayIndicators);
      } else {
        // 回退：扩大到近365天重新获取，避免个别标的数据为空
        try {
          const retryBody = {
            symbol: stock.symbol,
            start_date: dayjs().subtract(365, 'day').format('YYYY-MM-DD'),
            end_date: dayjs().format('YYYY-MM-DD'),
            period: '1d'
          };
          const resp2 = await fetch(`${API_BASE_URL}/stocks/historical`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(retryBody)
          });
          const json2 = await resp2.json();
          const transformedData2: PriceData[] = (json2.data || []).map((item: any) => ({
            timestamp: item.timestamp || item.date,
            value: parseFloat(item.close) || 0,
            open: parseFloat(item.open) || 0,
            high: parseFloat(item.high) || 0,
            low: parseFloat(item.low) || 0,
            close: parseFloat(item.close) || 0,
            volume: parseFloat(item.volume) || 0
          })).sort((a: PriceData, b: PriceData) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
          setKlineData(transformedData2);
          const indicators2: IndicatorData[] = transformedData2.map((item, index) => {
            const closes = transformedData2.slice(Math.max(0, index - 59), index + 1).map(d => d.close);
            const ma = (n: number) => (index >= n-1 ? closes.slice(-n).reduce((a,b)=>a+b,0)/n : undefined);
            return {
              timestamp: item.timestamp,
              value: item.close,
              ma5: ma(5),
              ma10: ma(10),
              ma20: ma(20),
              ma50: ma(50)
            } as any;
          });
          setIndicatorData(indicators2);
        } catch (e) {
          console.warn('历史数据格式不正确或为空:', result);
          setKlineData([]);
          setIndicatorData([]);
        }
      }
      
    } catch (err: any) {
      console.error('K线数据处理失败:', err);
      setKlineData([]);
      message.error(`历史数据加载失败: ${err.message || '请稍后重试'}`);
    } finally {
      setKlineLoading(false);
    }
  }, [API_BASE_URL]);

  // 执行分析函数
  const performAnalysis = useCallback(async (stock: StockInfo, parameters: AnalysisParameters) => {
    if (!stock || !parameters) return;
    
    setLoading(true);
    setStrategyLoading(false);
    setError('');
    
    // 启动 Sequence 避免旧请求覆盖
    const seq = analysisSeqRef.current + 1;
    analysisSeqRef.current = seq;

    try {
      // --- 第一阶段：快速加载 (Lite信息 + K线数据) ---
      // 并行启动 K线请求 和 Lite信息请求
      const klineTask = fetchKlineData(stock, parameters);
      
      const liteTask = (async () => {
          try {
              const resp = await fetch(`${API_BASE_URL}/analysis/lite/${stock.symbol}`);
              if (resp.ok) {
                  return await resp.json();
              }
          } catch (e) { console.warn("Lite analysis fetch failed", e); }
          return null;
      })();

      // 等待快速信息 (Lite优先展示，K线并行)
      let liteData = null;
      try {
          const [_, lite] = await Promise.all([klineTask, liteTask]);
          liteData = lite;
      } catch (e) {
          console.warn("Fast phase data fetch incomplete", e);
      }
      
      // 如果 Sequence 变了，说明有新请求，丢弃当前结果
      if (seq !== analysisSeqRef.current) return;

      // 使用 Lite 数据构建“部分结果”以快速渲染 UI
      if (liteData) {
          // 尝试从 Lite 数据中提取涨跌幅
          let priceChangePct = 0;
          if (typeof liteData.change_pct === 'string') {
              priceChangePct = parseFloat(liteData.change_pct.replace('%', ''));
          } else if (typeof liteData.change_pct === 'number') {
              priceChangePct = liteData.change_pct;
          }

          const partialResult: AnalysisResult = {
              symbol: liteData.symbol,
              company_name: stock.name, 
              timestamp: liteData.timestamp || new Date().toISOString(),
              overall_signal: liteData.signal || 'HOLD',
              confidence: (typeof liteData.ai_score === 'number') ? liteData.ai_score / 100 : 0.5,
              risk_level: 'MEDIUM', // 默认
              expected_return: 0,
              strategies: [], // 暂时为空
              market_data: {
                  current_price: liteData.price,
                  price_change: 0, 
                  price_change_percent: priceChangePct,
                  market_cap: 0, 
                  pe_ratio: 0,
                  pb_ratio: 0
              },
              technical_indicators: {}, 
              risk_metrics: {}
          };
          setAnalysisResult(partialResult);
      }

      // 第一阶段完成，UI 解锁，展示基础信息
      setLoading(false);

      // --- 第二阶段：全量策略分析 ---
      setStrategyLoading(true);

      // 构建请求参数，包含策略权重
      const tau = localStorage.getItem('PARAM_TAU') || '0.2';
      const alpha = localStorage.getItem('PARAM_ALPHA') || '0.5';
      const beta = localStorage.getItem('PARAM_BETA') || '0.6';
      const useGroupAgg = (localStorage.getItem('USE_GROUP_AGG') || '1');
      const queryParams = new URLSearchParams({
        market: 'A',
        period: '1d',
        days: (() => {
          try {
            const dr = parameters.dateRange;
            if (dr && dr[0] && dr[1]) {
              const start = dr[0]; const end = dr[1];
              const diff = end.diff(start, 'day');
              return String(Math.max(1, diff));
            }
          } catch {}
          return '365';
        })(),
        // 添加策略权重参数
        trend_following: (parameters.strategyWeights.trend_following / 100).toString(),
        mean_reversion: (parameters.strategyWeights.mean_reversion / 100).toString(),
        statistical_arbitrage: (parameters.strategyWeights.statistical_arbitrage / 100).toString(),
        volatility_trading: (parameters.strategyWeights.volatility_trading / 100).toString(),
        market_sentiment: (parameters.strategyWeights.market_sentiment / 100).toString(),
        ml_forecasting: (parameters.strategyWeights.ml_forecasting / 100).toString(),
        money_flow: (parameters.strategyWeights.money_flow / 100).toString(),
        risk_parity: (parameters.strategyWeights.risk_parity / 100).toString(),
        sector_rotation: (parameters.strategyWeights.sector_rotation / 100).toString(),
        fundamental_quant: (parameters.strategyWeights.fundamental_quant / 100).toString(),
        enhanced_event: (parameters.strategyWeights.enhanced_event / 100).toString(),
        event_driven: (parameters.strategyWeights.event_driven / 100).toString(),
        grid_trading: (parameters.strategyWeights.grid_trading / 100).toString(),
        factor_investing: (parameters.strategyWeights.factor_investing / 100).toString()
      });

      // 从设置读取阈值与组聚合开关
      queryParams.append('tau', tau);
      queryParams.append('alpha', alpha);
      queryParams.append('beta', beta);
      queryParams.append('use_group_aggregation', useGroupAgg);
      const rcUp = localStorage.getItem('PRICE_LIMIT_UP');
      const rcDown = localStorage.getItem('PRICE_LIMIT_DOWN');
      const rcSym = localStorage.getItem('PRICE_LIMIT_PCT');
      const rcSlip = localStorage.getItem('SLIPPAGE_BPS');
      const rcFill = localStorage.getItem('FILL_RATE');
      if (rcUp) queryParams.append('limit_up_pct', rcUp);
      if (rcDown) queryParams.append('limit_down_pct', rcDown);
      if (rcSym) queryParams.append('price_limit_pct', rcSym);
      if (rcSlip) queryParams.append('slippage_bps', rcSlip);
      if (rcFill) queryParams.append('fill_rate', rcFill);
      
      // 配对股票：强制优先读取本地存储（来自右侧“对标股票”选择），覆盖 analysisParameters 中的旧值
      {
        let ps = '';
        if (selectedStock) {
          try {
            const sym = String(selectedStock.symbol || '').trim();
            let localPs = String(localStorage.getItem(`PAIRED_SYMBOL::${sym}`) || '');
            if (!localPs && sym.includes('.')) {
              const base = sym.split('.')[0];
              localPs = String(localStorage.getItem(`PAIRED_SYMBOL::${base}`) || '');
            }
            if (!localPs && !sym.includes('.')) {
              localPs = String(localStorage.getItem(`PAIRED_SYMBOL::${sym}.SH`) || '');
              if (!localPs) localPs = String(localStorage.getItem(`PAIRED_SYMBOL::${sym}.SZ`) || '');
            }
            if (localPs) ps = localPs;
          } catch { void 0; }
        }
        if (!ps) {
          try { ps = String((analysisParameters as any)?.pairedSymbol || ''); } catch { ps = ''; }
        }
        ps = ps.trim();
        if (ps) {
          const norm = ((val: string) => {
            const s = String(val || '').trim().toUpperCase();
            if (!s) return s;
            if (/^\d{6}$/.test(s)) {
              if (s.startsWith('5')) return `${s}.SH`;
              if (s.startsWith('1')) return `${s}.SZ`;
              return `${s}.${s.startsWith('6') ? 'SH' : 'SZ'}`;
            }
            return s;
          })(ps);
          queryParams.append('paired_symbol', norm);
          const zEntry = localStorage.getItem('STAT_ARB_Z_ENTRY');
          const zExit = localStorage.getItem('STAT_ARB_Z_EXIT');
          const lookback = localStorage.getItem('STAT_ARB_LOOKBACK');
          if (zEntry) queryParams.append('stat_arb_z_entry', zEntry);
          if (zExit) queryParams.append('stat_arb_z_exit', zExit);
          if (lookback) queryParams.append('stat_arb_lookback', lookback);
          const daysVal = (() => {
            try {
              const lb = lookback ? parseInt(String(lookback)) : 365;
              const withBuf = lb + 30;
              return String(Math.max(365, Math.min(withBuf, 720)));
            } catch { return '365'; }
          })();
          queryParams.append('days', daysVal);
        }
      }
      
      const useGroupRiskComposite = (localStorage.getItem('USE_GROUP_RISK_COMPOSITE') || '1');
      queryParams.append('use_group_risk_composite', useGroupRiskComposite);
      const finalConfFromGroups = (localStorage.getItem('FINAL_CONF_FROM_GROUPS') || '0');
      const finalRiskFromGroups = (localStorage.getItem('FINAL_RISK_FROM_GROUPS') || '0');
      queryParams.append('final_confidence_from_groups', finalConfFromGroups);
      queryParams.append('final_risk_from_groups', finalRiskFromGroups);
      const mnSensitivity = (localStorage.getItem('MARKET_NEUTRAL_SENSITIVITY') || '1.0');
      queryParams.append('market_neutral_sensitivity', mnSensitivity);

      // 传递启用策略列表（可选）
      if (parameters.selectedStrategies && parameters.selectedStrategies.length > 0) {
        queryParams.append('strategies', parameters.selectedStrategies.join(','));
      }
      
      const req = fetch(`${API_BASE_URL}/analysis/strategy/${stock.symbol}?${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Tushare-Token': (localStorage.getItem('TUSHARE_TOKEN') || '')
        }
      });
      const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), 75000));
      const response = await Promise.race<[Response | null]>([req as any, timeout as any]) as any;
      
      if (response === null) throw new Error('timeout');
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.detail || 'Strategy analysis failed');
      }
      
      const json = await response.json();
      if (seq !== analysisSeqRef.current) return;

      const transformedResult = transformAnalysisData(json, undefined);
      setAnalysisResult(transformedResult);

      // 异步叠加财务估值
      (async () => {
        try {
          const req = fetch(`${API_BASE_URL}/stocks/financial/${stock.symbol}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', 'X-Tushare-Token': (localStorage.getItem('TUSHARE_TOKEN') || '') }
          });
          const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), 3500));
          const finResp = await Promise.race<[Response | null]>([req as any, timeout as any]) as any;
          if (finResp && finResp.ok) {
            const overlay = await finResp.json();
            const merged = transformAnalysisData(json, overlay);
            setAnalysisResult(merged);
          }
        } catch { void 0; }
      })();

    } catch (err: any) {
      console.error('分析流程异常:', err);
      // 如果第一阶段成功了，仅提示错误而不清空结果
      setAnalysisResult(prev => {
         if (prev) {
             message.warning(`策略深度分析失败: ${err.message || '超时'}`);
             return prev;
         }
         setError(err.message || '未知错误');
         // 返回降级占位符
         return {
            symbol: stock.symbol,
            company_name: stock.name || stock.symbol,
            timestamp: new Date().toISOString(),
            overall_signal: 'HOLD',
            confidence: 0,
            risk_level: 'MEDIUM',
            expected_return: 0,
            strategies: [],
            market_data: {},
            technical_indicators: {},
            risk_metrics: {}
         } as any;
      });
    } finally {
      setLoading(false);
      setStrategyLoading(false);
    }
  }, [API_BASE_URL, fetchKlineData, transformAnalysisData]);

  // 处理股票选择
  const handleStockSelect = useCallback((stock: StockInfo) => {
    setSelectedStock(stock);
    if (analysisParameters) {
      performAnalysis(stock, analysisParameters);
    }
  }, [analysisParameters, performAnalysis]);

  // 处理参数变更
  const handleParametersChange = useCallback((parameters: any) => {
    // 兼容不同模块的AnalysisParameters类型定义，统一为本组件的结构
    const casted: AnalysisParameters = parameters as AnalysisParameters;
    setAnalysisParameters(casted);
    if (selectedStock) {
      performAnalysis(selectedStock, casted);
    }
  }, [selectedStock, performAnalysis]);

  // 手动刷新分析
  const handleRefresh = useCallback(() => {
    if (selectedStock && analysisParameters) {
      performAnalysis(selectedStock, analysisParameters);
    }
  }, [selectedStock, analysisParameters, performAnalysis]);

  // 技术指标相关回调函数
  const handleIndicatorTypesChange = useCallback((types: string[]) => {
    setIndicatorTypes(types);
  }, []);

  const handleShowVolumeChange = useCallback((checked: boolean) => {
    setShowVolume(checked);
  }, []);

  const handleShowIndicatorsChange = useCallback((checked: boolean) => {
    setShowIndicators(checked);
  }, []);

  useEffect(() => {
    if (!selectedStock && analysisParameters && AUTO_LOAD_LAST_STOCK) {
      try {
        const saved = typeof window !== 'undefined' ? localStorage.getItem('recentStocks') : '';
        if (saved) {
          const list = JSON.parse(saved || '[]');
          if (Array.isArray(list) && list.length > 0) {
            const s = list[0];
            setSelectedStock(s);
            performAnalysis(s, analysisParameters);
            return;
          }
        }
      } catch { void 0; }
    }
  }, [selectedStock, analysisParameters, performAnalysis]);

  // 自动更新逻辑
  useEffect(() => {
    if (autoUpdateTimer) {
      clearInterval(autoUpdateTimer);
    }

    if (analysisParameters?.autoUpdate && selectedStock && analysisParameters.updateInterval > 0) {
      const timer = setInterval(() => {
        // 在更新前检查 token 是否存在，避免无 token 轮询
        const token = localStorage.getItem('TUSHARE_TOKEN');
        if (token) {
          performAnalysis(selectedStock, analysisParameters);
        }
      }, analysisParameters.updateInterval * 1000);
      
      setAutoUpdateTimer(timer);
    }

    return () => {
      if (autoUpdateTimer) {
        clearInterval(autoUpdateTimer);
      }
    };
  }, [analysisParameters, selectedStock, performAnalysis]);

  // 环境检测（仅一次）
  useEffect(() => {
    (async () => {
      const tauri = await isTauriAvailable();
      setTauriAvailable(tauri);
      // 自动尝试启动后端：若检测到 Tauri 环境且本地存在 Token，则调用桌面启动命令
      // 说明：点击设置保存后也会再次调用；此处用于首次打开应用时自动拉起服务
      try {
        const token = typeof window !== 'undefined' ? (localStorage.getItem('TUSHARE_TOKEN') || '') : '';
        if (tauri && token) {
          const res = await startBackends(token);
          if (res.ok) {
            message.success(res.message || '后端服务已启动');
          } else {
            message.info(res.message || '无法自动启动后端，请在设置中手动重试');
          }
        }
      } catch (err) { void 0; }
    })();
  }, []);

  // 当选中股票但名称缺失时，回退拉取名称并更新头部显示
  useEffect(() => {
    (async () => {
      if (selectedStock && selectedStock.name === selectedStock.symbol) {
        try {
          const rt = await analysisApi.getRealtimeData(selectedStock.symbol);
          if (rt && rt.name) {
            setSelectedStock({ ...selectedStock, name: rt.name });
          }
        } catch (_) { void 0; }
      }
    })();
  }, [selectedStock]);

  return (
    <Layout className="three-column-layout">
      {/* 左侧栏：搜索和配置 */}
      <Sider 
        width={360}
        collapsed={leftCollapsed}
        collapsedWidth={0}
        className="left-sider"
        theme="light"
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 100,
          borderRight: '1px solid #f0f0f0'
        }}
      >
        <div className="sider-content">
          <div className="sider-header">
            <Space align="center">
              <SettingOutlined style={{ fontSize: '18px', color: 'var(--primary-blue)' }} />
              <Title level={4} style={{ margin: 0, color: 'var(--primary-blue)' }}>
                搜索配置
              </Title>
            </Space>
            <Tooltip title="收起侧栏">
              <Button 
                type="text" 
                size="small"
                onClick={() => setLeftCollapsed(true)}
                style={{ marginLeft: 'auto' }}
              >
                ←
              </Button>
            </Tooltip>
          </div>
          
          <Divider style={{ margin: '12px 0' }} />
          
          {/* 股票搜索组件 */}
          <Card 
            title="股票搜索" 
            size="small" 
            style={{ marginBottom: 16 }}
            styles={{ body: { padding: '12px' } }}
          >
            <StockSearch 
              onStockSelect={handleStockSelect}
              selectedStock={selectedStock}
            />
          </Card>
          
          {/* 参数设置组件 */}
          <Card 
             title="分析参数" 
             size="small"
             styles={{ body: { padding: '16px' } }}
           >
             <ParameterSettings 
               onParametersChange={handleParametersChange}
             />
           </Card>
        </div>
      </Sider>

      {/* 主内容区域 */}
      <Layout style={{ marginLeft: leftCollapsed ? 0 : 360, marginRight: rightCollapsed ? 0 : 400 }}>
        <Content className="main-content">
          {/* 顶部工具栏 */}
          <div className="content-header">
            <Space>
              <Button type="default" size="small" onClick={() => setSettingsVisible(true)}>
                设置
              </Button>
              <Button type="text" size="small" icon={<LogoutOutlined />} onClick={handleLogout} danger>
                退出
              </Button>
              {leftCollapsed && (
                <Tooltip title="展开搜索配置">
                  <Button 
                    type="text" 
                    icon={<SettingOutlined />}
                    onClick={() => setLeftCollapsed(false)}
                  >
                    搜索配置
                  </Button>
                </Tooltip>
              )}
              
              <Space align="center" style={{ gap: '12px' }} className="brand-header">
                {/* 品牌标识：圆角矩形，品牌蓝色背景，白色文字 - 优化尺寸比例 */}
                <Avatar shape="square" size={48} className="brand-logo" style={{ 
                  backgroundColor: 'var(--primary-blue)', 
                  borderRadius: 12, 
                  color: '#fff', 
                  fontWeight: 600, 
                  fontSize: '20px',
                  fontFamily: 'PingFang SC, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  boxShadow: '0 2px 6px rgba(24, 144, 255, 0.3)',
                  lineHeight: 1
                }}>
                  知是
                </Avatar>
                <Title level={3} className="brand-title" style={{ 
                  margin: 0, 
                  color: '#1F6FDB',
                  fontSize: '28px',
                  fontWeight: 600,
                  fontFamily: 'PingFang SC, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  lineHeight: 1.2,
                  letterSpacing: '-0.3px'
                }}>
                  图表数据
                </Title>
              </Space>
              
              {rightCollapsed && (
                 <Tooltip title="展开策略分析">
                  <Button 
                    type="text" 
                    icon={<RiseOutlined />}
                    onClick={() => setRightCollapsed(false)}
                  >
                    策略分析
                  </Button>
                </Tooltip>
              )}
              {(() => {
                const t = (typeof localStorage !== 'undefined' ? localStorage.getItem('TUSHARE_TOKEN') : null) || '';
                const tokenOk = !!t && t.trim().length > 0;
                const ok = tokenOk || hasToken;
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Tag color={ok ? 'green' : 'red'}>{ok ? 'Token已就绪' : 'Token未就绪'}</Tag>
                    <Space size={8}>
                      <Badge status={backendReady ? 'success' : 'error'} text="后端" />
                      <Badge status={mcpReady ? 'success' : 'error'} text="MCP" />
                      <Badge status={tauriAvailable ? 'processing' : 'success'} text={tauriAvailable ? 'Web+Tauri' : 'Web'} />
                    </Space>
                  </div>
                );
              })()}
            </Space>
            
            <Space>
              {selectedStock && (
                <Text strong>
                  {selectedStock.symbol} - {selectedStock.name}
                </Text>
              )}
              
              <Tooltip title="刷新分析">
                <Button 
                  type="primary" 
                  icon={<ReloadOutlined />}
                  loading={loading}
                  onClick={handleRefresh}
                  disabled={!selectedStock || !analysisParameters}
                >
                  刷新
                </Button>
              </Tooltip>
            </Space>
          </div>

          <Divider style={{ margin: '16px 0' }} />

          {/* 主动推荐（ESG + Smart Beta） */}
          <div style={{ marginBottom: 16 }}>
            <React.Suspense fallback={<div className="loading-container"><Spin size="small" /><Text style={{ marginLeft: 8 }}>加载推荐模块...</Text></div>}>
              <RecommendationsPanel />
            </React.Suspense>
          </div>

          {/* 图表显示区域 */}
          <div className="chart-container">
            {error && (
              <Alert
                message="分析错误"
                description={error}
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
                action={
                  <Button size="small" onClick={handleRefresh}>
                    重试
                  </Button>
                }
              />
            )}
            
            {loading && (
              <div className="loading-container">
                <Spin size="large" />
                <Text style={{ marginTop: 16, display: 'block', textAlign: 'center' }}>
                  正在分析数据...
                </Text>
              </div>
            )}
            
            {!loading && !error && selectedStock && (
              <React.Suspense fallback={<div className="loading-container"><Spin size="large" /><Text style={{ marginTop: 16, display: 'block', textAlign: 'center' }}>正在加载图表模块...</Text></div>}>
              <Tabs
                activeKey={activeChartTab}
                onChange={setActiveChartTab}
                items={[
                  {
                    key: 'advanced',
                    label: (
                      <span>
                        <BarChartOutlined />
                        高级K线图
                      </span>
                    ),
                    children: (
                      <AdvancedCandlestickChart
                        data={klineData}
                        indicators={indicatorData}
                        showVolume={showVolume}
                        showIndicators={showIndicators}
                        indicatorTypes={indicatorTypes}
                        onDataPointClick={(data) => console.log('Data point clicked:', data)}
                        onZoomChange={(range) => console.log('Zoom changed:', range)}
                      />
                    ),
                  },
                  {
                    key: 'indicators',
                    label: (
                      <span>
                        <LineChartOutlined />
                        技术指标
                      </span>
                    ),
                    children: (
                      <TechnicalIndicatorChart
                       data={indicatorData}
                       indicators={indicatorTypes}
                     />
                    ),
                  },
                  {
                    key: 'composite',
                    label: (
                      <span>
                        <DotChartOutlined />
                        组合图表
                      </span>
                    ),
                    children: (
                      <CompositeChart
                        data={klineData.map(item => ({
                           timestamp: item.timestamp,
                           value: item.close,
                           volume: item.volume
                         }))}
                         showBrush={true}
                         onDataPointClick={(data) => console.log('Composite clicked:', data)}
                      />
                    ),
                  },
                  {
                    key: 'original',
                    label: (
                      <span>
                        <BarChartOutlined />
                        原始图表
                      </span>
                    ),
                    children: (
                      <PriceChart 
                        data={klineData}
                        loading={klineLoading}
                        showVolume={showVolume}
                        showIndicators={showIndicators}
                        indicatorTypes={indicatorTypes}
                        onIndicatorTypesChange={handleIndicatorTypesChange}
                        onShowVolumeChange={handleShowVolumeChange}
                        onShowIndicatorsChange={handleShowIndicatorsChange}
                      />
                    ),
                  },
                  {
                    key: 'backtest',
                    label: (
                      <span>
                        <LineChartOutlined />
                        回测实验室
                      </span>
                    ),
                    children: (
                      <Card size="small" title="多品种RSI动量回测" extra={<Button type="primary" onClick={runBacktest} loading={backtestLoading}>运行回测</Button>}>
                        <Space wrap size={12} style={{ marginBottom: 12 }}>
          <Form layout="inline" initialValues={backtestParams} onValuesChange={(_, values) => setBacktestParams(prev => ({ ...prev, ...values }))}>
            <Form.Item label="仓位比例%" name="percents">
              <InputNumber min={1} max={100} />
            </Form.Item>
            <Form.Item label="止损比例" name="stop_loss_dist">
              <InputNumber min={0.01} max={0.2} step={0.01} />
            </Form.Item>
            <Form.Item label="SMA周期" name="sma_period">
              <InputNumber min={50} max={400} />
            </Form.Item>
            <Form.Item label="RSI周期" name="rsi_period">
              <InputNumber min={7} max={28} />
            </Form.Item>
            <Form.Item label="基准" name="benchmark">
              <Input style={{ width: 140 }} />
            </Form.Item>
          </Form>
      </Space>
                        <Text type="secondary">说明：不选择日期时默认最近365天。SMA周期与RSI周期为指标窗口，不是日期长度。</Text>
                        {backtestResult && (
                          <Space direction="vertical" size={12} style={{ width: '100%' }}>
                            <Alert type="success" showIcon message={`累计收益: ${(backtestResult.performance?.cumulative_return*100||0).toFixed(2)}% | 夏普: ${(backtestResult.performance?.sharpe_ratio||0).toFixed(2)} | 最大回撤: ${(backtestResult.performance?.max_drawdown*100||0).toFixed(2)}%`} />
                            {Array.isArray(backtestResult.chart_images) && backtestResult.chart_images[0] ? (
                              <img alt="回测资金曲线" src={backtestResult.chart_images[0]} style={{ width: '100%', borderRadius: 8 }} />
                            ) : (
                              <div id="backtest-chart-capture">
                                <CompositeChart
                                  data={(backtestResult.equity_curve || []).map((e:any)=>({ timestamp: e.timestamp, value: e.value }))}
                                  showBrush={true}
                                />
                              </div>
                            )}
                            {(!backtestResult.chart_images || backtestResult.chart_images.length === 0) && (!backtestResult.equity_curve || backtestResult.equity_curve.length === 0) && (
                              <Text type="secondary">暂无数据：请扩大日期范围或调整RSI/SMA参数。</Text>
                            )}
                          </Space>
                        )}
                      </Card>
                    ),
                  },
                ]}
              />
              </React.Suspense>
            )}
            
            {!selectedStock && !loading && (
              <div className="empty-state">
                <BarChartOutlined style={{ fontSize: '64px', color: '#d9d9d9' }} />
                <Title level={4} style={{ color: '#999', marginTop: 16 }}>
                  请选择股票开始分析
                </Title>
                <Text type="secondary">
                  在左侧搜索栏中输入股票代码或名称
                </Text>
              </div>
            )}
          </div>
        </Content>
      </Layout>

      {/* 设置弹窗：Token 与缓存TTL（桌面环境下可启动后端） */}
      <Modal
        title="设置（后端与数据源）"
        open={settingsVisible}
        onOk={async () => {
          try {
            const values = await form.validateFields();
            const ttl = String(values.finValTTL || '900');
            localStorage.setItem('FIN_VAL_TTL', ttl);
            // 阈值与组聚合设置保存
            localStorage.setItem('PARAM_TAU', String(values.tau ?? '0.2'));
            localStorage.setItem('PARAM_ALPHA', String(values.alpha ?? '0.5'));
            localStorage.setItem('PARAM_BETA', String(values.beta ?? '0.6'));
            localStorage.setItem('USE_GROUP_AGG', (values.useGroupAgg ? '1' : '0'));
            localStorage.setItem('USE_GROUP_RISK_COMPOSITE', (values.useGroupRiskComposite ? '1' : '0'));
            // 组层口径用于最终置信与风险
            if (typeof values.finalConfFromGroups !== 'undefined') {
              localStorage.setItem('FINAL_CONF_FROM_GROUPS', (values.finalConfFromGroups ? '1' : '0'));
            }
            if (typeof values.finalRiskFromGroups !== 'undefined') {
              localStorage.setItem('FINAL_RISK_FROM_GROUPS', (values.finalRiskFromGroups ? '1' : '0'));
            }
            localStorage.setItem('MARKET_NEUTRAL_SENSITIVITY', String(values.mnSensitivity ?? '1.0'));
            
            // 立即触发一次分析以让新阈值与组聚合开关生效
            if (selectedStock && analysisParameters) {
              performAnalysis(selectedStock, analysisParameters);
            }
            setSettingsVisible(false);
          } catch (e: any) {
            message.error(`保存设置失败：${e?.message || '未知错误'}`);
          }
        }}
        onCancel={() => setSettingsVisible(false)}
      >
        <Form form={form} layout="vertical" preserve={false} initialValues={{ finValTTL: initialTTL, tau: Number(localStorage.getItem('PARAM_TAU') || '0.2'), alpha: Number(localStorage.getItem('PARAM_ALPHA') || '0.5'), beta: Number(localStorage.getItem('PARAM_BETA') || '0.6'), useGroupAgg: (localStorage.getItem('USE_GROUP_AGG') || '1') === '1', useGroupRiskComposite: (localStorage.getItem('USE_GROUP_RISK_COMPOSITE') || '1') === '1', finalConfFromGroups: (localStorage.getItem('FINAL_CONF_FROM_GROUPS') || '0') === '1', finalRiskFromGroups: (localStorage.getItem('FINAL_RISK_FROM_GROUPS') || '0') === '1', mnSensitivity: Number(localStorage.getItem('MARKET_NEUTRAL_SENSITIVITY') || '1.0') }}>
          
          <Alert 
            type="info" 
            showIcon 
            style={{ marginBottom: 16 }}
            message={'数据源 Token 已移至管理控制台统一配置，此处仅提供分析参数调整。'} 
          />
          
          <Form.Item label="估值缓存TTL(秒)" name="finValTTL" rules={[{ required: true, message: '请填写TTL秒数' }]}>
            <Input id="setting-ttl" placeholder="默认900（15分钟）" />
          </Form.Item>
          <Divider />
          <Title level={5}>阈值与组聚合</Title>
          <Space size={12} wrap>
            <Form.Item label="tau(中性阈值)" name="tau" rules={[{ required: true, message: '请填写tau' }]} help="当|加权推荐分|<tau时视为中性(观望)。取值范围[0,1]，建议0.15~0.30"> 
              <InputNumber id="setting-tau" min={0} max={1} step={0.01} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item label="alpha(置信下限)" name="alpha" rules={[{ required: true, message: '请填写alpha' }]} help="低于alpha则建议观望。取值范围[0,1]，建议0.4~0.6"> 
              <InputNumber id="setting-alpha" min={0} max={1} step={0.01} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item label="beta(风险上限)" name="beta" rules={[{ required: true, message: '请填写beta' }]} help="高于beta则建议观望。取值范围[0,1]，建议0.6~0.8"> 
              <InputNumber id="setting-beta" min={0} max={1} step={0.01} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item label="启用组聚合" name="useGroupAgg" valuePropName="checked">
              <Switch id="setting-use-group-agg" />
            </Form.Item>
            <Form.Item label="使用复合风险" name="useGroupRiskComposite" valuePropName="checked">
              <Switch id="setting-use-risk-comp" />
            </Form.Item>
            <Form.Item label="使用组置信替代最终置信" name="finalConfFromGroups" valuePropName="checked">
              <Switch id="setting-final-conf" />
            </Form.Item>
            <Form.Item label="使用组风险替代最终风险" name="finalRiskFromGroups" valuePropName="checked">
              <Switch id="setting-final-risk" />
            </Form.Item>
            <Form.Item label="市场中性组敏感度" name="mnSensitivity" help="仅在信号为HOLD/NEUTRAL时增强市场中性组强度刻度，默认1.0，建议≤1.5">
              <InputNumber id="setting-mn-sensitivity" min={0} max={3} step={0.1} style={{ width: 160 }} />
            </Form.Item>
          </Space>
          <Alert 
            type="info" 
            showIcon 
            message={'点击“确定”将尝试启动后端服务；若桌面接口不可用会返回错误提示，同时设置会保存到本地。'} 
          />
        </Form>
      </Modal>

      {/* 右侧栏：策略分析 */}
      <Sider 
        width={400}
        collapsed={rightCollapsed}
        collapsedWidth={0}
        className="right-sider"
        theme="light"
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          right: 0,
          top: 0,
          bottom: 0,
          zIndex: 100,
          borderLeft: '1px solid #f0f0f0'
        }}
      >
        <div className="sider-content">
          <div className="sider-header">
            <Tooltip title="收起侧栏">
              <Button 
                type="text" 
                size="small"
                onClick={() => setRightCollapsed(true)}
              >
                →
              </Button>
            </Tooltip>
           <Space align="center">
               <RiseOutlined style={{ fontSize: '18px', color: 'var(--primary-blue)' }} />
               <Title level={4} style={{ margin: 0, color: 'var(--primary-blue)' }}>
                 策略分析
               </Title>
               <Tooltip title="显示数据不足的策略占位">
                 <Space size={4}>
                   <Text type="secondary" style={{ fontSize: 12 }}>显示占位</Text>
                   <Switch size="small" checked={showPlaceholders} onChange={setShowPlaceholders} />
                 </Space>
               </Tooltip>
             </Space>
           </div>
           
           <Divider style={{ margin: '12px 0' }} />
           
            <AnalysisResults 
              result={analysisResult}
              loading={loading}
              selectedStrategies={analysisParameters?.selectedStrategies?.map(s => s.toString()) || []}
              showPlaceholders={showPlaceholders}
              klineSampleCount={klineData?.length || 0}
              latestTimestamp={klineData?.length ? klineData[klineData.length - 1]?.timestamp : undefined}
              windowDays={klineData?.length || undefined}
              onPeriodChange={(p:number)=> setExportPeriod(p)}
              onRefresh={handleRefresh}
            />

           
        </div>
      </Sider>
    </Layout>
  );
};

export default ThreeColumnLayout;
// 启动行为控制：是否自动加载并分析最近一次选择的股票
const AUTO_LOAD_LAST_STOCK = false;
